import styled from 'styled-components';
import { IconGraphy, Table, Tooltip, Button } from '@leapeasy/ui-kit';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { getDemoData } from 'utils/helpers';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { GlobalUXState } from 'components/GlobalUXState';
import {
  fitContentCompactColumnOptions,
  fitContentTextColumnOptions,
} from 'utils/fitContentTextColumnOptions';
import {
  INVITE_NEW_USER_TABLE_DEFAULT_VISIBLE_KEYS,
  INVITE_NEW_USER_TABLE_STORAGE_KEY,
} from './inviteNewUserTableConfig';

const LEAP_PURPLE = '#702572';

const dateColumnOptions = (overrides = {}) => ({
  width: 104,
  minWidth: 104,
  maxWidth: 104,
  shrink: false,
  ...overrides,
});

const formatLandlordAddress = (landlord) =>
  [
    landlord.billingStreet || '',
    landlord.billingCity || '',
    landlord.billingState || '',
    landlord.billingPostalCode || '',
  ]
    .join(' ')
    .trim();

export const InviteNewUserTable = forwardRef(
  (
    {
      landlords,
      filter,
      dropFilter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      onClickLandlord,
      onInviteLandlord,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
    },
    ref,
  ) => {
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));

    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));

    const buildInviteNewUserRow = useCallback(
      (landlord) => {
        const address = formatLandlordAddress(landlord);
        return [
          isDemo ? getDemoData('landlord-name') : landlord.name,
          isDemo ? getDemoData('email') : landlord.email_address,
          new Date(landlord.sf_createdDate).toISOString().slice(0, 10),
          landlord.total_buildings ? landlord.total_buildings : 0,
          landlord.total_units ? landlord.total_units : 0,
          isDemo ? getDemoData('address') : address,
          isDemo ? getDemoData('phone') : landlord.phone,
          landlord.id,
          landlord.id,
        ];
      },
      [isDemo],
    );

    const tableData = useMemo(() => {
      if (!landlords) return [];
      return landlords.map((landlord) => buildInviteNewUserRow(landlord));
    }, [buildInviteNewUserRow, landlords]);

    const goDetailPage = useCallback(
      (landlordId) => {
        const selectedLandlord = landlords.find((landlord) => landlord.id === landlordId);
        if (selectedLandlord) {
          onClickLandlord(selectedLandlord);
        }
      },
      [landlords, onClickLandlord],
    );

    const goInvitePage = useCallback(
      (landlordId) => {
        const selectedLandlord = landlords.find((landlord) => landlord.id === landlordId);
        if (selectedLandlord) {
          onInviteLandlord(selectedLandlord);
        }
      },
      [landlords, onInviteLandlord],
    );

    const columns = useMemo(
      () => [
        {
          name: 'Name',
          key: 'name',
          options: {
            minWidth: 140,
            flexGrow: 1,
            ellipsis: false,
            sort: true,
          },
        },
        {
          name: 'Email Address',
          key: 'email_address',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 160, maxWidth: 320, flexGrow: 0 }),
            sort: true,
          },
        },
        {
          name: 'Create date',
          key: 'create_date',
          options: {
            ...dateColumnOptions(),
            sort: true,
          },
        },
        {
          name: 'Property Count',
          key: 'total_buildings',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 120, align: 'center' }),
            sort: true,
          },
        },
        {
          name: 'Unit Count',
          key: 'total_units',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 72, maxWidth: 96, align: 'center' }),
            sort: true,
          },
        },
        {
          name: 'Address',
          key: 'billingStreet',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 360 }),
            sort: true,
          },
        },
        {
          name: 'Phone Number',
          key: 'phone_number',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 180 }),
            sort: true,
          },
        },
        {
          name: 'Invite User',
          key: 'invite_user',
          options: {
            width: 100,
            minWidth: 100,
            maxWidth: 100,
            shrink: false,
            sort: false,
            align: 'center',
            excludeFromExport: true,
            customBodyRenderer: (landlordId) => (
              <InviteUserCell>
                <Tooltip title="Invite user" placement="top" showCarot>
                  <span>
                    <Button
                      variant="secondary"
                      iconSuffix="EditorLayout.Send"
                      onClick={() => goInvitePage(landlordId)}
                    >
                      Invite
                    </Button>
                  </span>
                </Tooltip>
              </InviteUserCell>
            ),
          },
        },
        {
          name: 'Actions',
          key: 'actions',
          options: {
            width: 64,
            minWidth: 64,
            maxWidth: 64,
            shrink: false,
            sort: false,
            align: 'center',
            excludeFromExport: true,
            stickyRight: true,
            customBodyRenderer: (landlordId) => (
              <ActionsCell onClick={(e) => e.stopPropagation()}>
                <Tooltip title="View account details" placement="top" showCarot>
                  <ActionIconButton
                    type="button"
                    onClick={() => goDetailPage(landlordId)}
                    aria-label="View account details"
                  >
                    <IconGraphy icon="FileFolder.Description" style={{ color: LEAP_PURPLE }} />
                  </ActionIconButton>
                </Tooltip>
              </ActionsCell>
            ),
          },
        },
      ],
      [goDetailPage, goInvitePage],
    );

    return (
      <>
        <Table
          showEmptyState
          emptyContent={
            <GlobalUXState
              variant={filter?.searchText ? 'noResults' : 'noData'}
              compact
              description={
                filter?.searchText
                  ? 'No operators match your search.'
                  : 'No operators available for user invitation yet.'
              }
            />
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          onRequestExportData={
            onRequestExportData
              ? async () => {
                  const rows = await onRequestExportData();
                  return (rows || []).map((landlord) => buildInviteNewUserRow(landlord));
                }
              : undefined
          }
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const rows = await onRequestFullExportData();
                  return (rows || []).map((landlord) => buildInviteNewUserRow(landlord));
                }
              : undefined
          }
          data={tableData}
          autoTooltipForDefaultCells
          mobileWidth={0}
          defaultVisibleColumnKeys={INVITE_NEW_USER_TABLE_DEFAULT_VISIBLE_KEYS}
          columnVisibilityStorageKey={INVITE_NEW_USER_TABLE_STORAGE_KEY}
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          style={{ width: '100%' }}
          title="Invite Users"
          columns={columns}
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="Invite Users"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </>
    );
  },
);

InviteNewUserTable.displayName = 'InviteNewUserTable';

const InviteUserCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionsCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;
`;
